import { useState } from 'react'
import { Notice } from '../Toast'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createInstitution, getInstitutions, removeInstitutionLogo, uploadInstitutionLogo } from '../api/endpoints'
import type { InstitutionRow } from '../api/types'
import { ApiError } from '../api/client'
import { ErrorNotice } from '../components'
import HowItWorks from '../HowItWorks'
import { formatDateTime } from '../lib/format'

export default function InstitutionsPage() {
  const queryClient = useQueryClient()
  const list = useQuery({ queryKey: ['institutions'], queryFn: getInstitutions })
  const [form, setForm] = useState({ name: '', slug: '', adminName: '', adminEmail: '', adminPassword: '' })
  const [created, setCreated] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: () => createInstitution(form),
    onSuccess: async () => { setCreated(form.name); setForm({ name: '', slug: '', adminName: '', adminEmail: '', adminPassword: '' }); await queryClient.invalidateQueries({ queryKey: ['institutions'] }) },
  })
  const errs = create.error instanceof ApiError ? create.error.fields : {}
  const E = (k: string) => (errs[k] ? <p role="alert" className="muted" style={{ color: 'var(--danger)', margin: '2px 0 8px' }}>{errs[k]}</p> : null)
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value })
  return (
    <div>
      <h1>Institutions</h1>
      <HowItWorks id="institutions" title="How institutions work" steps={[
        { icon: 'institutions', title: 'One per organisation', text: 'Each institution has its own staff, assessments, programs, campaigns, candidates and results. None of it is visible to another.' },
        { icon: 'staff', title: 'A first administrator', text: 'Creating an institution also creates its first super admin. Give them the password in person.' },
        { icon: 'shield', title: 'They set it up', text: 'They add consent wording, create assessments and programs, then open a campaign.' },
      ]} />
      <ErrorNotice error={list.error} />
      <div className="table-wrap"><table>
        <thead><tr><th>Institution</th><th>Short name</th><th className="num">Staff</th><th>Logo</th><th>Created</th></tr></thead>
        <tbody>{list.data?.map((i) => <tr key={i.id}><td><b>{i.name}</b></td><td>{i.slug}</td><td className="num">{i.staffCount}</td><td><LogoCell inst={i} /></td><td>{formatDateTime(i.createdAt)}</td></tr>)}</tbody>
      </table></div>
      <form className="card" style={{ marginTop: 16, maxWidth: 520 }} onSubmit={(e) => { e.preventDefault(); setCreated(null); create.mutate() }}>
        <h2>Add an institution</h2>
        <label className="field">Name<input required value={form.name} onChange={set('name')} /></label>{E('name')}
        <label className="field">Short name for links<input required value={form.slug} onChange={set('slug')} placeholder="greenfield" /></label>{E('slug')}
        <label className="field">First administrator's name<input required value={form.adminName} onChange={set('adminName')} /></label>{E('adminName')}
        <label className="field">Their email<input type="email" required value={form.adminEmail} onChange={set('adminEmail')} /></label>{E('adminEmail')}
        <label className="field">Their first password (12+ characters)<input type="password" minLength={12} required autoComplete="new-password" value={form.adminPassword} onChange={set('adminPassword')} /></label>
        <ErrorNotice error={Object.keys(errs).length === 0 ? create.error : null} />
        {created && <Notice tone="ok">{created} was created.</Notice>}
        <button type="submit" className="primary" disabled={create.isPending}>{create.isPending ? 'Creating…' : 'Create institution'}</button>
      </form>
    </div>
  )
}

/** An institution's optional logo, set here by the platform administrator (or by that institution's own admins in Settings). */
function LogoCell({ inst }: { inst: InstitutionRow }) {
  const queryClient = useQueryClient()
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['institutions'] })
  const upload = useMutation({ mutationFn: (f: File) => uploadInstitutionLogo(inst.id, f), onSuccess: refresh })
  const remove = useMutation({ mutationFn: () => removeInstitutionLogo(inst.id), onSuccess: refresh })
  return (
    <div className="row" style={{ gap: 8 }}>
      {inst.hasLogo
        ? <img src={`/api/public/institutions/${inst.slug}/logo?v=${encodeURIComponent(inst.logoUpdatedAt ?? '')}`} alt={`${inst.name} logo`} style={{ maxHeight: 32, maxWidth: 90, background: '#fff', padding: 3, borderRadius: 6 }} />
        : <span className="muted">None</span>}
      <label className="btn">{upload.isPending ? 'Uploading…' : inst.hasLogo ? 'Replace' : 'Upload'}
        <input type="file" accept="image/png,image/jpeg" hidden disabled={upload.isPending} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload.mutate(f) }} />
      </label>
      {inst.hasLogo && <button type="button" className="danger" disabled={remove.isPending} onClick={() => remove.mutate()}>Remove</button>}
      <ErrorNotice error={upload.error ?? remove.error} />
    </div>
  )
}
